import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, ProgressBar, Badge, Spinner, Modal, Form, Alert } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import axios from '../utils/axios';
import { useAuth } from '../utils/AuthContext';

// API base URL - use same API base as rest of app
const API_URL = '';

// Initial metrics with zero values
const initialMetrics = [
  { label: 'Total Meetings', value: '0', trend: '0%', icon: 'bi-calendar-check' },
  { label: 'Hours Saved', value: '0.0', trend: '0%', icon: 'bi-clock-history' },
  { label: 'Tasks Created', value: '0', trend: '0%', icon: 'bi-list-check' },
  { label: 'Key Insights', value: '0', trend: '0%', icon: 'bi-lightbulb' }
];

const integrationPlatforms = [
  { name: 'Salesforce', connected: false, icon: 'bi-lightning' },
  { name: 'Slack', connected: true, icon: 'bi-slack' },
  { name: 'Microsoft Teams', connected: true, icon: 'bi-microsoft-teams' },
  { name: 'Asana', connected: false, icon: 'bi-kanban' },
  { name: 'HubSpot', connected: false, icon: 'bi-diagram-3' }
];

const Dashboard = () => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [meetings, setMeetings] = useState([]);
  const [metrics, setMetrics] = useState(initialMetrics);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [showAllMeetings, setShowAllMeetings] = useState(false);
  
  // State for renaming functionality
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [renameLoading, setRenameLoading] = useState(false);
  const [renameError, setRenameError] = useState('');

  // Calculate metrics based on meeting data
  const calculateMetrics = (meetingsData) => {
    const updatedMetrics = [...initialMetrics];
    
    try {
      // 1. Total Meetings - count the actual meetings
      const completedMeetings = meetingsData.filter(m => m.status === 'completed').length;
      updatedMetrics[0].value = meetingsData.length.toString();
      
      // 2. Hours Saved - calculate based on meeting durations (if available)
      let totalMinutes = 0;
      meetingsData.forEach(meeting => {
        if (meeting.duration) {
          // If duration is a string like "30 min", extract the number
          const durationMatch = meeting.duration.match(/(\d+)/);
          if (durationMatch && durationMatch[1]) {
            totalMinutes += parseInt(durationMatch[1], 10);
          } else if (typeof meeting.duration === 'number') {
            totalMinutes += meeting.duration;
          }
        }
      });
      
      // Convert minutes to hours with one decimal place
      const hoursSaved = (totalMinutes / 60).toFixed(1);
      updatedMetrics[1].value = hoursSaved;
      
      // 3. Tasks Created - count tasks if available, or estimate based on meetings
      // Let's estimate 2 tasks per completed meeting
      const tasksCreated = completedMeetings * 2;
      updatedMetrics[2].value = tasksCreated.toString();
      
      // 4. Key Insights - estimate based on completed meetings (3 insights per meeting)
      const keyInsights = completedMeetings * 3;
      updatedMetrics[3].value = keyInsights.toString();
      
      // Calculate trends (sample calculation - in a real app you'd compare to previous period)
      if (meetingsData.length > 0) {
        updatedMetrics[0].trend = '+100%';  // New meetings trend
        updatedMetrics[1].trend = '+100%';  // Hours saved trend
        updatedMetrics[2].trend = '+100%';  // Tasks created trend
        updatedMetrics[3].trend = '+100%';  // Key insights trend
      }
      
      return updatedMetrics;
    } catch (err) {
      console.error('Error calculating metrics:', err);
      return initialMetrics;
    }
  };

  // Create a memoized fetchMeetings function
  const fetchMeetings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get user ID from context
      const userId = currentUser?.id;
      
      if (!userId) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }
      
      console.log(`Fetching meetings for user: ${userId}`);
      
      // Fetch meetings from API
      const response = await axios.get(`/api/meetings`, {
        params: { userId }
      });
      
      console.log('API Response:', response.data);
      
      let meetingsData = [];
      
      // Handle different response formats
      if (response.data.success && response.data.meetings) {
        // Standard format
        meetingsData = response.data.meetings;
      } else if (Array.isArray(response.data)) {
        // Alternative format where response is directly an array
        meetingsData = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // Last resort - try to find an array property
        const possibleArrayProps = Object.values(response.data).filter(val => Array.isArray(val));
        if (possibleArrayProps.length > 0) {
          meetingsData = possibleArrayProps[0];
        }
      }
      
      if (meetingsData.length > 0) {
        console.log(`Found ${meetingsData.length} meetings`);
        setMeetings(meetingsData);
        
        // Calculate metrics based on the meetings data
        const updatedMetrics = calculateMetrics(meetingsData);
        setMetrics(updatedMetrics);
      } else {
        console.log('No meetings found or empty response');
        // Even with no meetings, update the UI to show zeros
        setMeetings([]);
        setMetrics(initialMetrics);
      }
    } catch (err) {
      console.error('Error fetching meetings:', err);
      setError('Error fetching meetings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Function to handle renaming a meeting
  const handleRenameMeeting = async () => {
    if (!selectedMeeting || !newTitle.trim()) return;
    
    setRenameLoading(true);
    setRenameError('');
    
    try {
      // Call the API to update the meeting title
      const meetingId = selectedMeeting.id || selectedMeeting._id;
      const response = await axios.put(`/api/meetings/${meetingId}`, {
        title: newTitle.trim()
      });
      
      if (response.data.success) {
        // Update the meeting in the local state
        const updatedMeetings = meetings.map(meeting => {
          const currentId = meeting.id || meeting._id;
          if (currentId === meetingId) {
            return { ...meeting, title: newTitle.trim() };
          }
          return meeting;
        });
        
        setMeetings(updatedMeetings);
        setShowRenameModal(false);
        setSelectedMeeting(null);
        setNewTitle('');
      } else {
        setRenameError('Failed to rename meeting. Please try again.');
      }
    } catch (err) {
      console.error('Error renaming meeting:', err);
      setRenameError('Error updating meeting. Please try again.');
    } finally {
      setRenameLoading(false);
    }
  };

  // Fetch data when component mounts, when currentUser changes, or when refreshCounter changes
  useEffect(() => {
    fetchMeetings();
    
    // Force a refresh of the meetings data every 30 seconds instead of 10
    // to reduce server load but still keep data fresh
    const refreshInterval = setInterval(() => {
      console.log('Refreshing meetings data...');
      fetchMeetings();
    }, 30000);
    
    return () => clearInterval(refreshInterval);
  }, [fetchMeetings, refreshCounter]);

  // Detect when navigating back to Dashboard and refresh data
  useEffect(() => {
    // This will trigger a refresh whenever the user returns to the dashboard
    setRefreshCounter(prevCounter => prevCounter + 1);
  }, [location.key]);

  const getStatusBadge = (status, isStandaloneRecording) => {
    let badgeClass = '';
    let text = '';
    
    switch (status) {
      case 'completed':
        badgeClass = 'badge-success';
        text = 'Completed';
        break;
      case 'processing':
        badgeClass = 'badge-warning';
        text = 'Processing';
        break;
      case 'failed':
        badgeClass = 'badge-danger';
        text = 'Failed';
        break;
      default:
        badgeClass = 'badge-info';
        text = 'Pending';
    }
    
    // Add special badge for standalone recordings (screen captures)
    if (isStandaloneRecording) {
      return (
        <div>
          <Badge bg="dark" className={`${badgeClass} p-2 mb-1 d-block`} style={{ fontWeight: '600' }}>
            {text}
          </Badge>
          <Badge bg="info" className="p-2 d-block" style={{ fontWeight: '600' }}>
            Screen Recording
          </Badge>
        </div>
      );
    }
    
    return (
      <Badge bg="dark" className={`${badgeClass} p-2`} style={{ fontWeight: '600' }}>
        {text}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <Container fluid className="p-3 p-md-4 dashboard-content">
      <Row className="mb-3 mb-md-4">
        <Col xs={12}>
          <h1 className="text-white h3 h1-md">Dashboard</h1>
          <p className="text-white-50 small d-none d-md-block">Welcome back, {currentUser?.name || 'User'}! Here's an overview of your meeting insights.</p>
        </Col>
        <Col xs={12} className="mt-2 d-flex flex-wrap gap-2">
          <Button as={Link} to="/upload" variant="primary" size="sm" className="flex-grow-1 flex-md-grow-0 px-3">
            <i className="bi bi-cloud-upload me-1"></i>
            Upload
          </Button>
          <Button as={Link} to="/enhanced-recording" variant="outline-primary" size="sm" className="flex-grow-1 flex-md-grow-0 px-3">
            <i className="bi bi-mic-fill me-1"></i>
            Record
          </Button>
        </Col>
      </Row>

      {/* Metrics Cards - 2x2 grid on mobile, 4 columns on desktop */}
      <div className="dashboard-stats mb-3 mb-md-4">
        <Row className="g-2 g-md-3">
          {metrics.map((metric, index) => (
            <Col xs={6} md={3} key={index}>
              <Card className="glass-card h-100">
                <Card.Body className="p-2 p-md-4 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-auto">
                    <div>
                      <div className="d-flex align-items-center mb-1 mb-md-2">
                        <i className={`bi ${metric.icon} me-1 me-md-2 text-white`} style={{ fontSize: '1rem' }}></i>
                        <span className="text-white-50 small">{metric.label}</span>
                      </div>
                      <h3 className="mb-0 text-white h4 h2-md">{metric.value}</h3>
                    </div>
                    <Badge bg="dark" className="badge-success p-1 p-md-2 d-none d-md-inline" style={{ fontWeight: '600', fontSize: '0.7rem' }}>
                      {metric.trend}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Recent Meetings */}
      <Row className="mb-4">
        <Col xs={12} className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0 text-white">Recent Meetings</h4>
          {meetings.length > 4 && (
            <Button 
              variant="link" 
              onClick={() => setShowAllMeetings(!showAllMeetings)}
              className="text-decoration-none text-white"
            >
              {showAllMeetings ? 'Show Less' : 'View All'}
            </Button>
          )}
        </Col>
        
        {loading ? (
          <Col className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="text-white-50 mt-3">Loading your meetings...</p>
          </Col>
        ) : error ? (
          <Col>
            <Card className="glass-card">
              <Card.Body className="p-4 text-center">
                <i className="bi bi-exclamation-circle text-danger mb-3" style={{ fontSize: '2rem' }}></i>
                <p className="text-white">{error}</p>
                <Button variant="outline-primary" onClick={() => fetchMeetings()}>
                  Retry
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ) : meetings.length === 0 ? (
          <Col>
            <Card className="glass-card">
              <Card.Body className="p-4 text-center">
                <i className="bi bi-calendar-x mb-3 text-white-50" style={{ fontSize: '2rem' }}></i>
                <h5 className="text-white">No Meetings Yet</h5>
                <p className="text-white-50">Upload your first meeting recording to get started.</p>
                <Button as={Link} to="/upload" variant="primary">
                  <i className="bi bi-cloud-upload me-2"></i>
                  Upload Recording
                </Button>
              </Card.Body>
            </Card>
          </Col>
        ) : (
          (showAllMeetings ? meetings : meetings.slice(0, 4)).map((meeting, index) => (
            <Col xs={12} sm={6} lg={showAllMeetings ? 4 : 3} className="mb-2 mb-md-3" key={meeting.id || meeting._id || index}>
              <Card className="card-hover glass-card meeting-card-mobile">
                <Card.Body className="p-2 p-md-3" style={{ minHeight: 'auto' }}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="text-white mb-0 text-truncate" style={{ maxWidth: '70%' }}>{meeting.title || 'Untitled Meeting'}</h6>
                    <div className="d-flex align-items-center gap-2">
                      {getStatusBadge(meeting.status, meeting.isStandaloneRecording)}
                      <Button 
                        variant="link" 
                        className="p-0 text-white" 
                        title="Rename"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedMeeting(meeting);
                          setNewTitle(meeting.title || '');
                          setShowRenameModal(true);
                        }}
                      >
                        <i className="bi bi-pencil-fill small"></i>
                      </Button>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-white-50">
                      <i className="bi bi-clock me-1"></i>
                      {meeting.duration || 'N/A'} • {formatDate(meeting.meetingDate || meeting.createdAt)}
                    </small>
                    <Button 
                      as={Link} 
                      to={`/summary/${meeting.id || meeting._id}`} 
                      variant="outline-primary"
                      size="sm"
                      disabled={meeting.status !== 'completed'}
                    >
                      {meeting.status === 'completed' ? 'View' : '...'}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))
        )}
      </Row>

      {/* Integrations & Quick Tips - Hidden on mobile */}
      <Row className="d-none d-md-flex">
        <Col lg={7} md={12} className="mb-4">
          <Card className="glass-card h-100">
            <Card.Body className="p-4">
              <h4 className="mb-3 text-white">Getting Started with Echo Brief</h4>
              <p className="text-white-50">
                Upload your Microsoft Teams meeting recordings to get AI-powered
                summaries, action items, and key points automatically.
              </p>
              
              <h5 className="mb-3 mt-4 text-white">Complete Your Setup</h5>
              <div className="setup-checklist">
                <div className="d-flex align-items-center mb-3">
                  <Badge bg="success" className="me-3 p-2">
                    <i className="bi bi-check-lg"></i>
                  </Badge>
                  <span className="text-white">Create your account</span>
                </div>
                <div className="d-flex align-items-center mb-3">
                  <Badge bg="success" className="me-3 p-2">
                    <i className="bi bi-check-lg"></i>
                  </Badge>
                  <span className="text-white">Connect Microsoft Teams</span>
                </div>
                <div className="d-flex align-items-center">
                  <Badge bg={metrics[0].value !== '0' ? "success" : "secondary"} className="me-3 p-2">
                    <i className={`bi ${metrics[0].value !== '0' ? "bi-check-lg" : "bi-hourglass-split"}`}></i>
                  </Badge>
                  <span className="text-white">Upload your first recording</span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col lg={5} md={12} className="mb-4">
          <Card className="glass-card h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0 text-white">Integrations</h4>
                <Button 
                  as={Link}
                  to="/profile"
                  state={{ activeTab: 'integrations' }}
                  variant="link" 
                  className="text-white p-0"
                >
                  <i className="bi bi-gear"></i>
                </Button>
              </div>
              <div className="integrations-list">
                {integrationPlatforms.map((platform, index) => (
                  <div key={index} className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                      <div className="integration-icon me-3">
                        <i className={`bi ${platform.icon}`}></i>
                      </div>
                      <span className="text-white">{platform.name}</span>
                    </div>
                    <Button 
                      as={Link}
                      to="/profile"
                      state={{ activeTab: 'integrations' }}
                      variant={platform.connected ? "success" : "outline-primary"} 
                      size="sm"
                    >
                      {platform.connected ? 'Connected' : 'Connect'}
                    </Button>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Rename Meeting Modal */}
      <Modal show={showRenameModal} onHide={() => {
        setShowRenameModal(false);
        setRenameError('');
      }} centered backdrop="static" className="light-modal">
        <Modal.Header closeButton style={{ backgroundColor: 'white', borderBottom: '1px solid #dee2e6' }}>
          <Modal.Title style={{ color: '#212529' }}>Rename Meeting</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: 'white' }}>
          {renameError && (
            <Alert variant="danger" className="mb-3">
              {renameError}
            </Alert>
          )}
          <Form onSubmit={(e) => {
            e.preventDefault();
            if (newTitle.trim()) {
              handleRenameMeeting();
            }
          }}>
            <Form.Group className="mb-3">
              <Form.Label className="text-white">New Title</Form.Label>
              <Form.Control
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Enter a descriptive title"
                maxLength={100}
                autoFocus
                style={{ 
                  backgroundColor: 'white', 
                  color: '#212529', 
                  border: '1px solid #ced4da',
                  boxShadow: 'none'
                }}
              />
            </Form.Group>
            <Button type="submit" className="d-none">Submit</Button>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: 'white', borderTop: '1px solid #dee2e6' }}>
          <Button variant="outline-secondary" onClick={() => {
            setShowRenameModal(false);
            setRenameError('');
          }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleRenameMeeting}
            disabled={renameLoading || !newTitle.trim()}
          >
            {renameLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Dashboard;
